// NotePopover — the in-place glossary card opened by a task's chef-hat on
// the cooking screen (README §6.6 content, surfaced without leaving the
// running timeline). A non-modal popover: it overlays the task column
// rather than reflowing it, and dismisses on Escape, an outside click, or
// the close button.
//
// Purely presentational — the cooking pane resolves the task's refs to
// `entries` (see cooking/notes.ts) and owns the open/close state.

import { useEffect, useRef } from 'react';
import { theme } from '../theme';

export interface NotePopoverEntry {
  id: string;
  text: string;
  source: 'skill' | 'note' | 'hotspot';
}

const SOURCE_LABEL: Record<NotePopoverEntry['source'], string> = {
  skill: 'Technique',
  note: 'Note',
  hotspot: 'Hotspot',
};

export interface NotePopoverProps {
  /** Task title, used in the accessible name. */
  title: string;
  entries: NotePopoverEntry[];
  onClose: () => void;
}

export function NotePopover({ title, entries, onClose }: NotePopoverProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Move focus into the popover so Escape and screen-reader focus work.
    ref.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Notes for ${title}`}
      tabIndex={-1}
      style={{
        position: 'absolute',
        top: 'calc(100% + 0.25rem)',
        left: 0,
        zIndex: 20,
        maxWidth: 'min(22rem, 90vw)',
        background: theme.bg.raised,
        color: theme.text.primary,
        border: `1px solid ${theme.bg.muted}`,
        borderRadius: '10px',
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.18)',
        padding: '0.75rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.6rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Techniques &amp; terms</span>
        <button
          type="button"
          aria-label="Close notes"
          onClick={onClose}
          style={{
            width: '32px',
            height: '32px',
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: theme.text.muted,
            cursor: 'pointer',
            fontSize: '1.1rem',
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.6rem',
        }}
      >
        {entries.map((entry) => (
          <li key={entry.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            <span
              style={{
                fontSize: '0.7rem',
                color: theme.text.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {SOURCE_LABEL[entry.source]}
            </span>
            <span style={{ lineHeight: 1.5 }}>{entry.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default NotePopover;
